import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  QrCode,
  Smartphone,
  CreditCard,
  BarChart3,
  Users,
  Clock,
  CheckCircle,
  ArrowRight,
  Star,
  Calendar,
  Shield,
  X
} from 'lucide-react';

const Home = () => {
  const { isAuthenticated } = useAuth();
  const { t, currentLanguage } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const features = [
    {
      icon: <QrCode className="h-10 w-10" />,
      title: t('home.features.qrCodeMenus.title'),
      description: t('home.features.qrCodeMenus.description'),
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: <Smartphone className="h-10 w-10" />,
      title: t('home.features.mobileOrdering.title'),
      description: t('home.features.mobileOrdering.description'),
      gradient: "from-green-500 to-emerald-500"
    },
    {
      icon: <CreditCard className="h-10 w-10" />,
      title: t('home.features.digitalPayments.title'),
      description: t('home.features.digitalPayments.description'),
      gradient: "from-purple-500 to-violet-500"
    },
    {
      icon: <BarChart3 className="h-10 w-10" />,
      title: t('home.features.realTimeAnalytics.title'),
      description: t('home.features.realTimeAnalytics.description'),
      gradient: "from-orange-500 to-amber-500"
    },
    {
      icon: <Users className="h-10 w-10" />,
      title: t('home.features.staffManagement.title'),
      description: t('home.features.staffManagement.description'),
      gradient: "from-red-500 to-rose-500"
    },
    {
      icon: <Clock className="h-10 w-10" />,
      title: t('home.features.orderTracking.title'),
      description: t('home.features.orderTracking.description'),
      gradient: "from-indigo-500 to-blue-500"
    }
  ];

  const benefits = t('home.benefits.list');
  const safeBenefits = Array.isArray(benefits) ? benefits : [];

  const stats = [
    { value: "100+", label: t('home.stats.restaurants') },
    { value: "1K+", label: t('home.stats.ordersProcessed') },
    { value: "98%", label: t('home.stats.customerSatisfaction') },
    { value: "45%", label: t('home.stats.fasterService') }
  ];

  // Fix: Properly handle trial features array
  const getTrialFeatures = () => {
    if (currentLanguage === 'fr') {
      return [
        "Accès complet à toutes les fonctionnalités",
        "Menus QR code illimités",
        "Tableau de bord d'analyse en temps réel",
        "Support client prioritaire",
        "Annulation facile à tout moment"
      ];
    }

    if (currentLanguage === 'nl') {
      return [
        "Volledige toegang tot alle functies",
        "Ongelimiteerde QR-code menu's",
        "Real-time analytics dashboard",
        "Prioritaire klantenservice",
        "Geen creditcard vereist",
        "Eenvoudig op elk moment opzegbaar"
      ];
    }

    if (currentLanguage === 'de-ch') {
      return [
        "Voller Zugang zu allen Funktionen",
        "Unbegrenzte QR-Code-Menüs",
        "Echtzeit-Analyse-Dashboard",
        "Prioritärer Kundensupport",
        "Keine Kreditkarte erforderlich",
        "Einfache Kündigung jederzeit"
      ];
    }

    // English fallback
    return [
      "Full access to all features",
      "Unlimited QR code menus",
      "Real-time analytics dashboard",
      "Priority customer support",
      "No credit card required",
      "Easy cancellation anytime"
    ];
  };

  const trialFeatures = getTrialFeatures();


  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* Trial Modal */}
      {showTrialModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 relative animate-scale-in">
            <button
              onClick={() => setShowTrialModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>

            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t('home.trial.modal.title')}
              </h3>
              <p className="text-gray-600">
                {t('home.trial.modal.subtitle')}
              </p>
            </div>

            <div className="space-y-4 mb-6">
              {trialFeatures.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-blue-500 flex-shrink-0" />
                  <span className="text-gray-700">{feature}</span>
                </div>
              ))}
            </div>

            <div className="space-y-3">
              <Link
                to="/register"
                className="block w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-xl font-semibold text-center hover:shadow-lg transition-all duration-300"
                onClick={() => setShowTrialModal(false)}
              >
                {t('home.trial.modal.startTrial')}
              </Link>
              <button
                onClick={() => setShowTrialModal(false)}
                className="block w-full border border-gray-300 text-gray-700 py-3 rounded-xl font-semibold text-center hover:border-gray-400 transition-all duration-300"
              >
                {t('home.trial.modal.maybeLater')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Animated Background */}
      <div className="absolute inset-0 -z-10 h-full w-full bg-white bg-[linear-gradient(to_right,#f0f0f0_1px,transparent_1px),linear-gradient(to_bottom,#f0f0f0_1px,transparent_1px)] bg-[size:6rem_4rem]">
        <div className="absolute bottom-0 left-0 right-0 top-0 bg-[radial-gradient(circle_800px_at_100%_200px,#d4e6ff,transparent)]"></div>
      </div>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-32">
        <div className="container relative">
          <div className={`text-center max-w-4xl mx-auto transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
            {/* Updated Badge with Trial Info */}
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-full text-sm font-medium mb-8">
              <Calendar className="h-4 w-4" />
              {t('home.trial.badge')}
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight break-words">
              {t('home.title')}
            </h1>

            <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed max-w-3xl mx-auto">
              {t('home.subtitle')}
            </p>

            {/* Trial Benefits */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm">
                <Calendar className="h-4 w-4" />
                {t('home.trial.trust.freeDays')}
              </div>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-8 mb-12">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-gray-900">{stat.value}</div>
                  <div className="text-gray-600 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              {isAuthenticated() ? (
                <Link
                  to="/dashboard"
                  className="group relative bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center"
                >
                  {t('home.goToDashboard')}
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              ) : (
                <>
                  <button
                    onClick={() => setShowTrialModal(true)}
                    className="group relative bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center"
                  >
                    {t('home.trial.cta.startTrial')}
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                </>
              )}
            </div>

            {/* Trust indicators */}
            <div className="mt-8 text-sm text-gray-500 flex items-center justify-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {t('home.trial.trust.free30Days')}
              </div>
              <div className="flex items-center gap-2">
                <X className="h-4 w-4" />
                {t('home.trial.trust.cancelAnytime')}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 lg:py-28">
        <div className="container">
          <div className={`text-center mb-20 transition-all duration-700 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Calendar className="h-4 w-4" />
              {t('home.trial.featuresIncluded')}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('home.features.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t('home.features.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100 ${isVisible ? 'animate-fade-in' : 'opacity-0'
                  }`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-300`}></div>
                <div className={`inline-flex items-center justify-center p-3 rounded-xl bg-gradient-to-br ${feature.gradient} text-white mb-6`}>
                  {feature.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
                <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${feature.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300`}></div>
              </div>
            ))}
          </div>

          {/* Trial CTA after features */}
          {!isAuthenticated() && (
            <div className="text-center mt-16">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  {t('home.trial.cta.readyToTransform')}
                </h3>
                <p className="text-gray-600 mb-6 text-lg">
                  {t('home.trial.cta.startToday')}
                </p>
                <button
                  onClick={() => setShowTrialModal(true)}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 inline-flex items-center"
                >
                  {t('home.trial.cta.startTrial')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className={`transition-all duration-700 delay-300 ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
              }`}>
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="h-5 w-5 text-blue-600" />
                <span className="text-blue-600 font-semibold">{t('home.trial.included')}</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">
                {t('home.benefits.title')}
              </h2>
              <p className="text-xl text-gray-600 mb-10 leading-relaxed">
                {t('home.benefits.subtitle')}
              </p>

              <ul className="space-y-5">
                {safeBenefits.map((benefit, index) => (
                  <li
                    key={index}
                    className="flex items-center bg-white/50 backdrop-blur-sm rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow duration-300"
                  >
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                      <CheckCircle className="h-6 w-6 text-blue-600" />
                    </div>
                    <span className="text-lg font-medium text-gray-800">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className={`transition-all duration-700 delay-500 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
              }`}>
              <div className="bg-white rounded-3xl shadow-2xl p-10 border border-gray-100 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-indigo-500/10 rounded-full -translate-y-16 translate-x-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-full -translate-x-12 translate-y-12"></div>

                <div className="relative">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="h-5 w-5 text-blue-600" />
                    <span className="text-blue-600 font-semibold">{t('home.trial.badge')}</span>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-6">
                    {t('home.cta.title')}
                  </h3>
                  <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                    {t('home.cta.subtitle')}
                  </p>

                  {/* Trial Benefits */}
                  <div className="space-y-3 mb-6">
                    {trialFeatures.slice(0, 3).map((feature, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <CheckCircle className="h-5 w-5 text-blue-500" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Testimonial */}
                  <div className="bg-gray-50 rounded-xl p-6 mb-8">
                    <div className="flex items-center mb-4">
                      <div className="flex text-amber-400">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="h-5 w-5 fill-current" />
                        ))}
                      </div>
                    </div>
                    <p className="text-gray-700 italic mb-4">
                      {t('home.cta.testimonial')}
                    </p>
                    <div className="text-sm font-medium text-gray-900">
                      {t('home.cta.testimonialAuthor')}
                    </div>
                  </div>

                  {!isAuthenticated() && (
                    <button
                      onClick={() => setShowTrialModal(true)}
                      className="block w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-center py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                    >
                      {t('home.trial.cta.startTrial')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="container relative text-center">
          <div className={`max-w-4xl mx-auto transition-all duration-700 delay-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
            <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Calendar className="h-4 w-4" />
              {t('home.trial.limitedOffer')}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t('home.trial.finalCta.title')}
            </h2>
            <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto leading-relaxed">
              {t('home.trial.finalCta.subtitle')}
            </p>
            {!isAuthenticated() && (
              <button
                onClick={() => setShowTrialModal(true)}
                className="inline-flex items-center bg-white text-blue-600 hover:bg-gray-50 font-bold text-lg px-10 py-5 rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105"
              >
                {t('home.trial.cta.startTrial')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            )}
            <div className="mt-6 text-blue-100 text-sm">
              {t('home.trial.finalCta.trust')}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;